import { IDepartment, Identifiable } from '../../../../lib/types';
import React, { useState } from 'react';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { TableWithActions } from '../../../_core/table/table-with-actions.component';
import { CreateDepartmentsContainer } from '../../../domains/departments/create/create-departments-form.container';
import { Button } from '../../../_core/button/button.component';
import { activeStatus } from '../../../../utils/wording.helpers';
import { UpdateDepartmentContainer } from '../../../domains/departments/update/update-department-form.container';
import { IAdminProps } from '../../../_core/core.types';
import { ITableHeaderCell, ITableOptionsConfig, ITableRowValues } from '../../../_core/table/table.types';
import { nameAndStatusSort, sortMethodTuple, useTableSortConfig } from '../../../_core/table/utils/table-sort';
import { IPermanent } from '../../../domains/request-group/request-group.types';
import { showError } from '../../../../lib/utils';
import { useConfirm } from '../../../_core/confirm/confirm.utils';
import { FetchResult } from '@apollo/client';

export interface IDepartmentTableProps extends IAdminProps, ITableOptionsConfig<IDepartment> {
  departments: IDepartment[];
}

interface IDepartmentTableActionProps {
  deleteDepartment: (_: Identifiable & IPermanent) => Promise<FetchResult<{ deleteDepartment: IDepartment }>>;
  deleting: boolean;
  restoreDepartment: (_: Identifiable) => void;
  restoring: boolean;
}

export const DepartmentTable: React.FC<IDepartmentTableProps & IDepartmentTableActionProps> = ({
  departments,
  deleteDepartment,
  deleting,
  restoreDepartment,
  restoring,
  ...rest
}) => {
  const [createDepartments, setCreateDepartments] = useState(false);
  const [updateDepartment, setUpdateDepartment] = useState<IDepartment | null>(null);

  const { ConfirmationDialog: PermanentlyDeleteConfirmationDialog, confirm: confirmPermanent } = useConfirm({
    title: 'Are you sure you want to permanently delete this location?',
    message: 'This action cannot be undone. Consider archiving instead.'
  });

  const headers: ITableHeaderCell[] = [
    { content: 'Name', sortKey: 'name' },
    { content: 'Status', sortKey: 'deletedAt' },
    { content: '' }
  ];

  const { sortFunc, ...restSort } = useTableSortConfig({
    defaultSortFunc: nameAndStatusSort,
    sortMethods: { name: sortMethodTuple(), deletedAt: sortMethodTuple('string', (d?: Date) => activeStatus(!d)) }
  });

  return (
    <>
      <TableWithActions
        {...rest}
        {...restSort}
        bordered
        headers={headers}
        values={
          departments.sort(sortFunc).map(({ _id, name, deletedAt }, i) => {
            const row: ITableRowValues = {
              values: [
                { children: name },
                { children: activeStatus(!deletedAt) },
                {
                  children: (
                    <Button
                      slim
                      loading={deleting || restoring}
                      size="large"
                      text="ACTION"
                      dropdown={{
                        options: [
                          { text: 'Edit', onClick: () => setUpdateDepartment(departments[i]) },
                          deletedAt
                            ? { text: 'Reactivate', onClick: () => restoreDepartment({ _id }) }
                            : {
                                text: 'Archive',
                                onClick: () =>
                                  deleteDepartment({ _id }).catch((err) =>
                                    showError('Unable to archive department', err)
                                  )
                              },
                          {
                            text: 'Delete',
                            onClick: () =>
                              confirmPermanent().then((confirmed) => {
                                if (confirmed)
                                  deleteDepartment({ _id, permanent: true }).catch((err) =>
                                    showError('Unable to delete department', err)
                                  );
                              })
                          }
                        ]
                      }}
                    />
                  )
                }
              ]
            };

            return row;
          }) ?? []
        }
      >
        <Button onClick={() => setCreateDepartments(true)} text="Add Departments" size="large" />
      </TableWithActions>
      <RIDialog open={createDepartments} setOpen={setCreateDepartments}>
        <CreateDepartmentsContainer close={() => setCreateDepartments(false)} />
      </RIDialog>
      {!!updateDepartment && (
        <RIDialog open={!!updateDepartment} setOpen={(o) => setUpdateDepartment((l) => (o ? l : null))}>
          <UpdateDepartmentContainer {...updateDepartment} />
        </RIDialog>
      )}
      <PermanentlyDeleteConfirmationDialog />
    </>
  );
};
